import React, { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
	SelectOption,
	SelectOptions,
} from "../../components/essentials/input/select/AcedSelect";
import StepNavigation from "../../components/essentials/step-navigation/StepNavigation";
import { selectUser } from "../../core/auth/authSlice";
import { JobApiRoutes } from "../../shared/api-routes/job/job-api.routes";
import { JobDraftApiRoutes } from "../../shared/api-routes/job/job-draft-api.routes";
import { User } from "../../shared/models";
import http from "../../shared/utils/http";
import "./CreateJob.scss";
import Step1BasicInfo from "./steps/Step1BasicInfo";
import Step2PersonalInfo from "./steps/Step2PersonalInfo";
import Step3SportsInfo from "./steps/Step3SportsInfo";
import Step4Description from "./steps/Step4Description";
import Step5Review from "./steps/Step5Review";

const CreateJob: React.FC = () => {
	const { accountType, actorId, userId } = useSelector(selectUser) as User;
	const params = useParams();

	// Load saved data from localStorage
	const getSavedFormData = () => {
		try {
			const saved = localStorage.getItem("createJobFormData");
			return saved ? JSON.parse(saved) : { description: "" };
		} catch (error) {
			console.error("Error loading saved form data:", error);
			return { description: "" };
		}
	};

	// Load saved step state
	const getSavedStepState = () => {
		try {
			const saved = localStorage.getItem("createJobStepState");
			if (saved) {
				const parsed = JSON.parse(saved);
				return {
					currentStep: parsed.currentStep || 1,
					visitedSteps: new Set<number>(
						(parsed.visitedSteps || [1]).map((v: unknown) => Number(v))
					),
				};
			}
		} catch (error) {
			console.error("Error loading saved step state:", error);
		}
		return {
			currentStep: 1,
			visitedSteps: new Set<number>([1]),
		};
	};

	const methods = useForm<Record<string, any>>({
		mode: "onSubmit",
		defaultValues: getSavedFormData(),
	});

	// Load draft data if draft_id is present
	useEffect(() => {
		if (params.draft_id) {
			http
				.get(JobDraftApiRoutes.GET_JOB_DRAFT_BY_ID(params.draft_id))
				.then(async (draftData) => {
					// Define the form fields that should be prefilled
					const formFields = [
						"subject",
						"city",
						"availableToTransfer",
						"sport",
						"position",
						"cartellinoStatus",
						"team",
						"owningTeam",
						"description",
					];

					// Prefill form with only the relevant draft data
					formFields.forEach((field) => {
						if (draftData[field] !== undefined && draftData[field] !== null) {
							methods.setValue(field, draftData[field]);
						}
					});

					// Handle league - if it's an ID, fetch the league details
					if (draftData.league) {
						if (typeof draftData.league === "string") {
							// League is an ID, fetch league details
							try {
								const leagueData = await http.get(
									`/utils/leagues/${draftData.league}`
								);
								if (leagueData) {
									methods.setValue("category", leagueData.category);
									methods.setValue("league", leagueData.name);
								}
							} catch (error) {
								console.error("Error loading league data:", error);
							}
						} else if (typeof draftData.league === "object") {
							// League is already an object with name and category
							if (draftData.league.category) {
								methods.setValue("category", draftData.league.category);
							}
							if (draftData.league.name) {
								methods.setValue("league", draftData.league.name);
							}
						}
					}

					// Handle standalone category field if it exists (for backwards compatibility)
					if (draftData.category && !draftData.league) {
						methods.setValue("category", draftData.category);
					}
				})
				.catch((error) => {
					console.error("Error loading draft data:", error);
				});
		}
	}, [params.draft_id, methods]);

	const { t } = useTranslation();
	const navigate = useNavigate();

	const savedStepState = getSavedStepState();
	const [currentStep, setCurrentStep] = useState(savedStepState.currentStep);
	const [visitedSteps, setVisitedSteps] = useState<Set<number>>(
		savedStepState.visitedSteps
	);
	const [subjectOptions, setSubjectOptions] = useState<SelectOptions>([]);

	// Save form data to localStorage
	const saveFormData = (data: any) => {
		try {
			localStorage.setItem("createJobFormData", JSON.stringify(data));
		} catch (error) {
			console.error("Error saving form data:", error);
		}
	};

	// Save step state to localStorage
	const saveStepState = (step: number, visited: Set<number>) => {
		try {
			localStorage.setItem(
				"createJobStepState",
				JSON.stringify({
					currentStep: step,
					visitedSteps: Array.from(visited),
				})
			);
		} catch (error) {
			console.error("Error saving step state:", error);
		}
	};

	// Watch form changes and save to localStorage
	const watchedValues = methods.watch();
	useEffect(() => {
		saveFormData(watchedValues);
	}, [watchedValues]);

	// Save step state whenever it changes
	useEffect(() => {
		saveStepState(currentStep, visitedSteps);
	}, [currentStep, visitedSteps]);

	// Clear saved data function
	const clearSavedData = () => {
		localStorage.removeItem("createJobFormData");
		localStorage.removeItem("createJobStepState");
	};

	// Add a close button handler that clears data and navigates away
	const handleClose = () => {
		clearSavedData();
		navigate(-1);
	};

	// Cleanup localStorage when user navigates away from the page
	useEffect(() => {
		const handleBeforeUnload = () => {
			// Clear data when user closes tab/window or navigates to different domain
			clearSavedData();
		};

		// Add event listener for page unload
		window.addEventListener("beforeunload", handleBeforeUnload);

		// Cleanup function that runs when component unmounts
		return () => {
			window.removeEventListener("beforeunload", handleBeforeUnload);
			// Also clear data when component unmounts and user navigated away
			const isNavigatingToJobPages =
				window.location.pathname.includes("/jobs/");
			if (!isNavigatingToJobPages) {
				clearSavedData();
			}
		};
	}, []);
	const totalSteps = 5;
	const stepTitles = [
		t("createJob.step1.title", "Basic Info"),
		t("createJob.step2.title", "Personal Info"),
		t("createJob.step3.title", "Sports Info"),
		t("createJob.step4.title", "Description"),
		t("createJob.step5.title", "Review"),
	];

	useEffect(() => {
		http.get("/utils/job/subjects").then((response) => {
			setSubjectOptions(
				response
					.filter((value: string) => {
						if (["medStaff", "techStaff", "management"].includes(accountType)) {
							methods.setValue("subject", "team");
							return value === "team";
						} else return !(accountType === value);
					})
					.map(
						(value: string) =>
							({
								value,
								label: t(`subjects.${value}`),
							} as SelectOption)
					)
			);
		});
	}, []);

	const nextStep = () => {
		if (currentStep < totalSteps) {
			const nextStepNumber = currentStep + 1;
			setCurrentStep(nextStepNumber);
			setVisitedSteps((prev) => new Set([...Array.from(prev), nextStepNumber]));
		}
	};

	const prevStep = () => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	};

	const goToStep = (step: number) => {
		// Only allow navigation to visited steps or steps with data
		if (visitedSteps.has(step) || hasStepData(step)) {
			setCurrentStep(step);
		}
	};

	const getStepCompletionStatus = (
		step: number
	): "complete" | "partial" | "incomplete" => {
		const formData = methods.getValues();
		const watchSubject = formData.subject;

		switch (step) {
			case 1:
				return !!formData.subject ? "complete" : "incomplete";
			case 2:
				// Check all possible fields for step 2 based on account type
				const step2Fields = [formData.city];
				const step2Complete = step2Fields.every((field) => !!field);
				const step2HasSome = step2Fields.some((field) => !!field);

				if (step2Complete) return "complete";
				if (step2HasSome) return "partial";
				return "incomplete";
			case 3:
				// Check all possible fields for step 3 based on subject type and account type
				const step3Fields = [];
				if (watchSubject === "team" || watchSubject === "athlete") {
					step3Fields.push(
						formData.sport,
						formData.category,
						formData.position,
						formData.league
					);
				} else if (watchSubject === "staff") {
					step3Fields.push(formData.position);
				} else {
					// For staff accounts (techStaff, medStaff, management)
					step3Fields.push(formData.sport, formData.category);
				}

				const step3Complete =
					step3Fields.length > 0 && step3Fields.every((field) => !!field);
				const step3HasSome = step3Fields.some((field) => !!field);

				if (step3Complete) return "complete";
				if (step3HasSome) return "partial";
				return "incomplete";
			case 4:
				return !!(formData.description && formData.description.trim() !== "")
					? "complete"
					: "incomplete";
			case 5:
				// Step 5 requires all previous steps to be complete
				const allPreviousComplete = [1, 2, 3, 4].every(
					(s) => getStepCompletionStatus(s) === "complete"
				);
				return allPreviousComplete ? "complete" : "incomplete";
			default:
				return "incomplete";
		}
	};

	const hasStepData = (step: number): boolean => {
		const status = getStepCompletionStatus(step);
		return status === "complete" || status === "partial";
	};

	const validateCurrentStep = () => {
		const { trigger } = methods;

		switch (currentStep) {
			case 1:
				return trigger(["subject"]);
			case 2:
				// Personal info validation
				return trigger(["city"]);
			case 3:
				// Sports info validation
				const watchSubject = methods.watch("subject");
				const fieldsToValidate: string[] = [];
				if (watchSubject === "team" || watchSubject === "athlete") {
					fieldsToValidate.push("sport", "category", "position");
				} else {
					// For staff accounts (techStaff, medStaff, management)
					fieldsToValidate.push("sport", "category");
				}
				return fieldsToValidate.length > 0
					? trigger(fieldsToValidate)
					: Promise.resolve(true);
			case 4:
				return trigger(["description"]);
			case 5:
				return Promise.resolve(true); // Review step doesn't need additional validation
			default:
				return Promise.resolve(true);
		}
	};

	const handleNext = async () => {
		const isValid = await validateCurrentStep();
		if (isValid) {
			nextStep();
		}
	};

	const onSubmit = (data: any, event: any) => {
		const action = event.nativeEvent.submitter.value;

		const body = {
			...data,
			creatorAccountType: (() => {
				if (["medStaff", "techStaff", "management"].includes(accountType))
					return "staff";
				else return accountType;
			})(),
		};

		body.description = data.description;

		// Format league as an object with name and category
		if (data.league && data.category) {
			body.league = {
				name: data.league,
				category: data.category,
			};
			// Remove category from body as it's now included in league object
			delete body.category;
		}

		switch (action) {
			case "publish": {
				(params.draft_id
					? http.post(JobDraftApiRoutes.PUBLISH_DRAFT(params.draft_id), body)
					: http.post(JobApiRoutes.PUBLISH_JOB, body)
				).then(() => {
					// Clear saved form data and step state after successful submission
					localStorage.removeItem("createJobFormData");
					localStorage.removeItem("createJobStepState");
					navigate(`/jobs/my-jobs/posted/${userId}`);
				});
				break;
			}
			case "draft": {
				const strippedBody = Object.fromEntries(
					Object.entries(body).filter(([_, value]) => value !== "")
				);
				(params.draft_id
					? http.patch(`/job-draft/${params.draft_id}`, strippedBody)
					: http.post(`/job-draft`, strippedBody)
				).then(() => {
					// Clear saved form data and step state after successful draft save
					localStorage.removeItem("createJobFormData");
					localStorage.removeItem("createJobStepState");
					navigate("/jobs/drafts");
				});
				break;
			}
		}
	};

	const renderCurrentStep = () => {
		switch (currentStep) {
			case 1:
				return <Step1BasicInfo subjectOptions={subjectOptions} />;
			case 2:
				return <Step2PersonalInfo />;
			case 3:
				return <Step3SportsInfo />;
			case 4:
				return <Step4Description />;
			case 5:
				return <Step5Review />;
			default:
				return null;
		}
	};

	const handleSubmitAction = () => {
		// Trigger form submission for the last step - Publish action
		const form = document.querySelector(".create-job-form") as HTMLFormElement;
		if (form) {
			const submitButton = document.createElement("button");
			submitButton.type = "submit";
			submitButton.value = "publish";
			submitButton.style.display = "none";
			form.appendChild(submitButton);
			submitButton.click();
			form.removeChild(submitButton);
		}
	};

	const handleDraftAction = () => {
		// Trigger form submission for the last step - Draft action
		const form = document.querySelector(".create-job-form") as HTMLFormElement;
		if (form) {
			const submitButton = document.createElement("button");
			submitButton.type = "submit";
			submitButton.value = "draft";
			submitButton.style.display = "none";
			form.appendChild(submitButton);
			submitButton.click();
			form.removeChild(submitButton);
		}
	};

	return (
		<>
			<FormProvider {...methods}>
				<div className="create-job">
					<p className="create-job-title">
						{params.draft_id ? t("createJob.completeDraft") : t("createJob.title")}
					</p>

					<StepNavigation
						currentStep={currentStep}
						totalSteps={totalSteps}
						stepTitles={stepTitles}
						onStepClick={goToStep}
						visitedSteps={visitedSteps}
						hasStepData={hasStepData}
						getStepCompletionStatus={getStepCompletionStatus}
						showNavigationButtons={true}
						onNext={handleNext}
						onPrev={prevStep}
						onSubmit={handleSubmitAction}
						onSecondarySubmit={handleDraftAction}
						nextLabel={t("common.next", "Next")}
						prevLabel={t("common.previous", "Previous")}
						submitLabel={t("createJob.buttons.publish", "Publish")}
						secondarySubmitLabel={t("createJob.buttons.draft", "Save as Draft")}
					>
						<form
							onSubmit={methods.handleSubmit(onSubmit)}
							className="create-job-form"
						>
							{renderCurrentStep()}
						</form>
					</StepNavigation>
				</div>
			</FormProvider>
		</>
	);
};

export default CreateJob;
